import {Card, Container, Group, Menu, rem, ScrollArea, Space, Tabs, Text, Title, UnstyledButton} from "@mantine/core";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback} from "react";
import {ContainerEntity} from "@ots-frontend/ots-data";
import {ContainerDetails} from "./ContainerDetails";
import {createContentStyles} from "../../../theme/useContentStyles";
import {IconChevronDown, IconTag} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {ContainerAction, ContainerActionMenuDropdown} from "../../menu/ContainerActionMenu/ContainerActionMenu";
import {ObjectChangeLogView} from "../common/ObjectChangeLogView/ObjectChangeLogView";
import {ResizeableContent} from "../../ResizableContent/ResizableContent";
import {Badge} from "../../badge/Badge/Badge";
import { Badge as CatalystBadge } from "catalyst";
import {useContainerAction} from "../../menu/ContainerActionMenu/useContainerAction";
import {EntityNotesView} from "../common/EntityNotesView/EntityNotesView";

const useStyles = createContentStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type ContainerProfileProps = PropsWithChildren<{
  container: ContainerEntity
}>

const tabs = [
  {
    key: "details",
    value: "Details"
  },
  {
    key: 'notes',
    value: 'Notes'
  },
  {
    key: 'history',
    value: 'Change Log'
  },
];

export const ContainerProfile = (props: ContainerProfileProps) => {
  const {container} = props;

  const {classes} = useStyles();

  const {t} = useTranslation()
  const navigate = useNavigate();
  const {state} = useLocation();

  const {
    onContainerActionClick
  } = useContainerAction()

  const _onContainerActionClick = useCallback((action: ContainerAction) => {
    onContainerActionClick(container, action)
  }, [container, onContainerActionClick])

  const items = tabs.map((tab) => (
    <Tabs.Tab value={tab.key} key={tab.key}>
      {tab.value}
    </Tabs.Tab>
  ));

  return (
    <ResizeableContent heightOffset={120}>
      {(size) => (
        <ScrollArea h={size.height} offsetScrollbars={true}>
          <Card mih={size.height} shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
            <Container fluid={true} className={classes.mainSection}>
              <Group position="apart">
                <Group spacing="xs">
                  <Title order={4}>Container: {container.name}</Title>
                  {!container.active &&
                    <Badge color="red">
                      {t('common.state.active.false', 'Inactive')}
                    </Badge>
                  }
                </Group>
                <Menu
                  width={260}
                  position="bottom-end"
                  transitionProps={{transition: "pop-top-right"}}
                  withinPortal
                >
                  <Menu.Target>
                    <UnstyledButton>
                      <Group spacing={7}>
                        <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                          {t("common.action.plural", "Actions")}
                        </Text>
                        <IconChevronDown size={rem(12)} stroke={1.5}/>
                      </Group>
                    </UnstyledButton>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <ContainerActionMenuDropdown
                      onActionClick={_onContainerActionClick}
                      container={container}/>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Container>

            <Card.Section>
              <Container fluid={true}>
                <Tabs
                  defaultValue={state?.tabValue ?? "details"}
                  onTabChange={(value) => navigate(`?id=${container.id}`, {replace: true, state: {tabValue: value}})}
                >
                  <Tabs.List>{items}</Tabs.List>
                  <Space h="md"/>
                  <Tabs.Panel value="details">
                    <ContainerDetails container={container}/>
                  </Tabs.Panel>
                  <Tabs.Panel value="notes">
                    <EntityNotesView entity={container}/>
                  </Tabs.Panel>
                  <Tabs.Panel value="history">
                    <ObjectChangeLogView entity={container}/>
                  </Tabs.Panel>
                </Tabs>
              </Container>
            </Card.Section>
          </Card>
        </ScrollArea>
      )}
    </ResizeableContent>
  );
};
